import { makeAutoObservable, observable } from 'mobx';
import { PageResult } from '../PageResult';
import { HttpBackend } from '../../Services/Http/HttpBackend';
import { createPageResult } from '../PageResult';

export type BodyPartSize = 'tiny' | 'small' | 'medium' | 'large' | 'huge';
export type BodyPartType = 'MUSCLE' | 'SINEW' | 'BONE';

// Raw JSON from backend
export type BodyPartJson = {
  id: string;
  identifier: string;
  type: BodyPartType;
  size: BodyPartSize;
  name: string;
  names: Record<string, string>;
  description?: string;
  latinName?: string;
  childrenIds: string[];
};

// Observable model type
export type BodyPart = Omit<BodyPartJson, 'names'> & {
  names: Map<string, string>;
  children?: BodyPart[];
  toJS(): BodyPartJson;
};

// Factory function
export function createBodyPart(json?: Partial<BodyPartJson>): BodyPart {
  return makeAutoObservable({
    id: json?.id ?? '',
    identifier: json?.identifier ?? '',
    size: json?.size ?? 'medium',
    name: json?.name ?? '',
    names: observable.map(json?.names || {}),
    description: json?.description,
    latinName: json?.latinName,
    type: json?.type || 'MUSCLE',
    childrenIds: json?.childrenIds ?? [],
    children: undefined as BodyPart[] | undefined,

    toJS(): BodyPartJson {
      return {
        id: this.id,
        identifier: this.identifier,
        type: this.type,
        size: this.size,
        latinName: this.latinName,
        name: this.name,
        names: Object.fromEntries(this.names),
        description: this.description,
        childrenIds: this.childrenIds.slice(),
      };
    },
  });
}

// Static API methods
export const BodyPartApi = {
  async getAll(ids: string[]): Promise<BodyPart[]> {
    if (ids.length > 0) {
      const data = await Promise.all(ids.map((id) => BodyPartApi.get(id)));
      return data.filter((b) => !!b) as BodyPart[];
    }
    return [];
  },

  get(id: string): Promise<BodyPart | undefined> {
    return HttpBackend.get(`/activity/exercise/bodypart/${id}`).then((res) =>
      res ? createBodyPart(res) : undefined,
    );
  },

  async flatten(id: string): Promise<string[]> {
    return HttpBackend.get(`/activity/exercise/bodypart/${id}/flatten`).then((res) => res ?? []);
  },

  async list(params?: any): Promise<BodyPart[]> {
    return HttpBackend.getOrCached('bodyparts', '/activity/exercise/bodypart/list', params).then(
      (result: BodyPartJson[]) => result?.map((r) => createBodyPart(r)) ?? [],
    );
  },

  async find(query?: string, page: number = 0, lang?: string, size: number = 30): Promise<PageResult<BodyPart>> {
    const data = await HttpBackend.get('/activity/exercise/bodypart', {
      query,
      page,
      lang,
      size,
      sort: 'name,ASC',
    });
    if (data) {
      data.content = ((data || {}).content || []).map((b: BodyPartJson) => createBodyPart(b));
      return createPageResult(data);
    }
    return createPageResult();
  },
};
